import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { VariablePill } from './VariablePillExtension';
import { VariableSuggestion } from './VariableSuggestion';
import { convertMarkdownVariablesToHTML } from './convertMarkdownToPills';
import { Label } from '@/components/ui/label';
import { Kbd } from '@/components/ui/kbd';
import 'tippy.js/dist/tippy.css';
import './variable-suggestion.css';

type SubjectLineEditorProps = {
  label?: string;
  defaultValue: string;
  onChange: (text: string) => void;
  placeholder?: string;
};

export interface SubjectLineEditorHandle {
  insertVariable: (variableKey: string) => void;
}

const SubjectLineEditor = forwardRef<SubjectLineEditorHandle, SubjectLineEditorProps>((
  {
    label = 'Subject Line',
    defaultValue,
    onChange,
    placeholder = 'Enter subject line...',
  },
  ref
) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable all formatting for subject line
        bold: false,
        italic: false,
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        // Keep paragraph and text
        paragraph: true,
        text: true,
        // Disable hardBreak (Enter key)
        hardBreak: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
      VariablePill, // Custom extension for variable pills
      VariableSuggestion.configure({
        isEmail: true,
      }),
    ],
    content: convertMarkdownVariablesToHTML(defaultValue),
    onUpdate: ({ editor }) => {
      // Get plain text with variable placeholders
      const text = editor.getText();
      onChange(text);

      const { from, to } = editor.state.selection;
      console.log('✏️ SUBJECT onUpdate - Cursor:', { from, to, text });
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;
      const { node, offset } = editor.state.selection.$from;
      console.log('🎯 SUBJECT Cursor Changed:', {
        from,
        to,
        nodeType: node?.type?.name,
        offset,
        isEmpty: from === to
      });
    },
    editorProps: {
      attributes: {
        class: 'subject-line-editor',
      },
      // Prevent new lines in subject
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  });

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    insertVariable: (variableKey: string) => {
      if (editor) {
        editor.chain().focus().insertVariablePill(variableKey).run();
      }
    },
  }));

  // Update editor content when defaultValue changes
  useEffect(() => {
    if (editor && defaultValue !== editor.getText()) {
      const htmlContent = convertMarkdownVariablesToHTML(defaultValue);
      editor.commands.setContent(htmlContent);
    }
  }, [defaultValue, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div>
      {/* Label */}
      {label && (
        <Label htmlFor="subject" className="text-xs mb-1.5 block">
          {label}
        </Label>
      )}

      {/* Editor Content */}
      <div className="border border-border rounded-md overflow-hidden">
        <style>{`
          .subject-line-editor {
            padding: 0.5rem 0.75rem;
            outline: none;
            min-height: 38px;
            max-height: 38px;
            font-size: 0.875rem;
            overflow: hidden;
          }
          .subject-line-editor p {
            margin: 0;
            display: inline;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .subject-line-editor .ProseMirror {
            max-width: 100%;
            overflow: hidden;
          }
          .subject-line-editor .is-editor-empty:first-child::before {
            color: hsl(var(--muted-foreground));
            content: attr(data-placeholder);
            position: absolute;
            pointer-events: none;
            white-space: nowrap;
            max-width: calc(100% - 1.5rem);
            overflow: hidden;
            text-overflow: ellipsis;
          }
          /* Variable pill styles - using custom NodeView */
          .subject-line-editor .variable-pill {
            color: rgb(59, 130, 246);
            font-size: 0.75rem;
          }
          .subject-line-editor .variable-pill:hover .pill-delete {
            display: inline-block !important;
          }
          .subject-line-editor .variable-pill .pill-delete:hover {
            color: rgb(239, 68, 68);
          }
        `}</style>
        <EditorContent editor={editor} />
      </div>

      {/* Helper Text */}
      <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
        Press <Kbd>/</Kbd> to insert variables
      </div>
    </div>
  );
});

SubjectLineEditor.displayName = 'SubjectLineEditor';

export default SubjectLineEditor;
